import { useCallback, useRef, useState } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';

import {
  baaRequiredAtom,
  complianceKitRequiredAtom,
  displayLanguageAtom,
  phoneNumberAtom,
  signupSourceAtom,
  webinarBookedOutAtom,
  webinarTimeslotAtom,
} from '@/atoms/demoAtoms';
import { isOnboardingSubmitInProgressAtom } from '@/atoms/onboardingAtoms';
import {
  affiliateCodeAtom,
  hasCompletedOnboardingAtom,
  refCodeAtom,
  referralCampaignAtom,
} from '@/atoms/userCreationAtoms';
import {
  GET_ONBOARDING_V2_VARIANT,
  IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED,
} from '@/components/constants/flags';
import {
  inviteTeamStepCompletedAtom,
  isEligibleForPracticeTrialModal,
  practiceTrialModalPendingAtom,
} from '@/features/growth/practice-invite-team-members/atoms';
import { usePracticeInvite } from '@/features/growth/practice-invite-team-members/usePracticeInvite';
import {
  resolveTemplateQualityGate,
  useTemplateQualityGate,
} from '@/features/templateBuilder/hooks/forms/useTemplateQualityGate';
import { useMutateBookMeeting } from '@/hooks/mutation/useMutateBookMeeting';
import { useMutateCreateTemplate } from '@/hooks/mutation/useMutateCreateTemplate';
import { useMutateUpdateUserRegion } from '@/hooks/mutation/useMutateUpdateUserRegion';
import { invalidateGamifyTrialExtension } from '@/hooks/query/useQueryGamifyTrialExtension';
import { GET_JOINED_TEAMS } from '@/hooks/query/useQueryJoinedTeams';
import { GET_USER, useQueryUser } from '@/hooks/query/useQueryUser';
import {
  buildRegionalUrl,
  getHostForRegion,
  needsRegionRedirect,
} from '@/lib/region-routing';
import { getUser } from '@/lib/utils';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  ErrorSeverity,
  ErrorType,
  OnboardingErrorStep,
} from '@/models/analytics/features/errors';
import { getSupportedLanguages } from '@/pages/api/scribeAPI';
import {
  clearStoredRefCode,
  clearStoredReferralCampaign,
} from '@/utils/affiliateTracking';
import { sanitizeErrorMessage } from '@/utils/analytics/trackErrorViewed';
import { logger } from '@/utils/logger/logger';
import { getHasBookedDemo } from '@/utils/onboardingDemoState';
import { resolveOnboardingExperience } from '@/utils/onboardingExperience';
import { reactQueryClient } from '@/utils/reactQueryClient';
import {
  filterSupportedLanguagesForInput,
  filterSupportedLanguagesForOutput,
} from '@/utils/supportedLanguageFilters';
import { toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';
import { type LanguageCode } from '@heidi/schema/ml-scribe';
import { useIntl } from 'react-intl';

import { track } from '@/lib/analytics/track';
import type { UseOnboardingAnalyticsReturn } from '@/models/analytics/features/useOnboardingAnalytics';
import { clearAuthOnboardAnswers, clearOnboardingProgress } from '@heidi/growth';
import { useUpdateUser } from '@heidi/workspace/data';
import { inviteTeamStepMessages } from '../components/personalization/steps/invite-team-step.messages';
import type { PersonalizationFormData } from '../personalizationTypes';
import {
  buildLanguageSessionSettingsPatch,
  buildOnboardingUpdateUserRequest,
  clearCachedOnboardingPendingMark,
  mapClinicSizeToOrganisationSize,
  saveGeneratedTemplateWithRetry,
  setUserRegionFromCountry,
  submitDeferredMeetingBookingIfAny,
  submitDeferredPracticeInvitesIfAny,
  submitDemoSignupIfEligible,
  submitRequestDemoIfEligible,
  submitTemplateFavoritesSafe,
  submitWebinarDemoLeadIfEligible,
  trackOnboardingCompletionAnalytics,
} from './onboardingCompletionOperations';

/** Polls joined-teams until BE-provisioned Practice team appears (or give up). */
const TEAM_SETTLE_ATTEMPTS = 5;
const TEAM_SETTLE_DELAY_MS = 200;
/** Hard cap so a stalled GET_JOINED_TEAMS refetch cannot block exit. */
const TEAM_SETTLE_DEADLINE_MS = 2500;

type JoinedTeamsCache = {
  joined_teams?: Array<{ team_id: string }>;
};

async function settlePracticeTeamOnce(): Promise<boolean> {
  for (let attempt = 0; attempt < TEAM_SETTLE_ATTEMPTS; attempt++) {
    await reactQueryClient.refetchQueries({ queryKey: [GET_JOINED_TEAMS] });
    const data = reactQueryClient.getQueryData<JoinedTeamsCache>([GET_JOINED_TEAMS]);
    if (data?.joined_teams?.[0]?.team_id) return true;
    if (attempt < TEAM_SETTLE_ATTEMPTS - 1) {
      await new Promise((resolve) => setTimeout(resolve, TEAM_SETTLE_DELAY_MS));
    }
  }
  return false;
}

async function settlePracticeTeam(): Promise<boolean> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      settlePracticeTeamOnce(),
      new Promise<boolean>((resolve) => {
        timeoutId = setTimeout(() => resolve(false), TEAM_SETTLE_DEADLINE_MS);
      }),
    ]);
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
}

interface UseOnboardingCompletionProps {
  formData: PersonalizationFormData;
  analytics: UseOnboardingAnalyticsReturn;
  submitFavorites: (additionalFavoriteIds?: string[]) => Promise<void>;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  /** The /auth/onboard page already created the user and set its region. */
  resumeAfterAuthOnboard?: boolean;
}

interface UseOnboardingCompletionReturn {
  completeOnboarding: () => Promise<boolean>;
  isSubmitting: boolean;
  error: Error | null;
}

export function useOnboardingCompletion({
  formData,
  analytics,
  submitFavorites,
  onSuccess,
  onError,
  resumeAfterAuthOnboard = false,
}: UseOnboardingCompletionProps): UseOnboardingCompletionReturn {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const { data: user } = useQueryUser();
  const { mutateAsync: mutateUpdateUser } = useUpdateUser();
  const { mutateAsync: mutateUpdateUserRegion } = useMutateUpdateUserRegion();
  const hasUpdatedRegion = useRef(false);
  const { mutateAsync: mutateCreateTemplate } = useMutateCreateTemplate({
    showToast: false,
  });
  const templateQualityGate = useTemplateQualityGate('onboarding');
  const { mutateAsync: mutateBookMeeting } = useMutateBookMeeting();
  const { sendInvites } = usePracticeInvite('onboarding_invite_step');
  const { t } = useTranslations();
  const intl = useIntl();

  // Prefer the phone number collected in the v2 onboarding RoleStep; fall back to the
  // v1 demo atom so users who entered a phone number during the v1 signup modal still
  // have it submitted on completion.
  const atomPhoneNumber = useAtomValue(phoneNumberAtom);
  const phoneNumber = formData.phoneNumber?.trim()
    ? formData.phoneNumber
    : atomPhoneNumber;
  const setDisplayLanguage = useSetAtom(displayLanguageAtom);
  const baaRequired = useAtomValue(baaRequiredAtom);
  const complianceKitRequired = useAtomValue(complianceKitRequiredAtom);
  const signupSource = useAtomValue(signupSourceAtom);
  const affiliateCode = useAtomValue(affiliateCodeAtom);
  const refCode = useAtomValue(refCodeAtom);
  const referralCampaign = useAtomValue(referralCampaignAtom);
  const webinarTimeslot = useAtomValue(webinarTimeslotAtom);
  const webinarBookedOut = useAtomValue(webinarBookedOutAtom);

  const { ctaOptIn, demoReason } = formData;
  const setHasCompletedOnboarding = useSetAtom(hasCompletedOnboardingAtom);
  const setIsOnboardingSubmitInProgress = useSetAtom(isOnboardingSubmitInProgressAtom);
  const setPracticeTrialModalPending = useSetAtom(practiceTrialModalPendingAtom);
  const setInviteTeamStepCompleted = useSetAtom(inviteTeamStepCompletedAtom);

  const completeOnboarding = useCallback(async () => {
    setIsSubmitting(true);
    setIsOnboardingSubmitInProgress(true);
    setError(null);

    try {
      const willSaveGeneratedTemplate = Boolean(
        formData.generatedTemplateStructure && formData.generatedTemplateName
      );
      if (
        willSaveGeneratedTemplate &&
        !(await resolveTemplateQualityGate(templateQualityGate, { kind: 'create' }))
      ) {
        return false;
      }

      const organisationSize = mapClinicSizeToOrganisationSize(formData.clinicSize);
      const isEvidenceLaunchEnabled =
        IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED();

      let refreshedRegion: string | undefined;
      try {
        if (!resumeAfterAuthOnboard && !hasUpdatedRegion.current) {
          refreshedRegion = await setUserRegionFromCountry(
            formData,
            mutateUpdateUserRegion
          );
          hasUpdatedRegion.current = true;
        }
      } catch (err) {
        logger.warn('Region update failed during onboarding, continuing:', err);
        // Silent (user-invisible) failure — still tracked so we can quantify
        // how often onboarding completes against a stale region.
        track({
          options: { amplitude: false },
          event_name: TrackingEventName.OnboardingErrorViewed,
          props: {
            step: OnboardingErrorStep.REGION_UPDATE,
            error_type: ErrorType.API_ERROR,
            error_message: sanitizeErrorMessage(
              err instanceof Error ? err.message : 'Unknown region-update error'
            ),
            severity: ErrorSeverity.WARNING,
            country: formData.country?.countryCode,
            onboarding_variant: GET_ONBOARDING_V2_VARIANT(),
          },
        });
      }

      let supportedTranscriptionLanguageCodes:
        | { input?: LanguageCode[]; output?: LanguageCode[] }
        | undefined;
      try {
        const { data: supportedLanguages } = await getSupportedLanguages();
        supportedTranscriptionLanguageCodes = {
          input: filterSupportedLanguagesForInput(supportedLanguages ?? []).map(
            (language) => language.code
          ),
          output: filterSupportedLanguagesForOutput(supportedLanguages ?? []).map(
            (language) => language.code
          ),
        };
      } catch (err) {
        logger.warn(
          'Failed to load supported transcription languages during onboarding; continuing',
          err
        );
      }

      const updateData = {
        ...buildOnboardingUpdateUserRequest({
          formData,
          organisationSize,
          phoneNumber,
          affiliateCode,
          refCode,
          referralCampaign,
          isEvidenceLaunchEnabled,
          signupSource,
        }),
        ...(resumeAfterAuthOnboard && { onboarding_completed: true }),
        ...buildLanguageSessionSettingsPatch(
          formData,
          supportedTranscriptionLanguageCodes
        ),
      };

      let userEmail: string | undefined = user?.email ?? undefined;
      try {
        const freshUser = await reactQueryClient.fetchQuery({
          queryKey: [GET_USER],
          queryFn: () => getUser().then((r) => r.data),
          staleTime: 0,
        });
        userEmail = freshUser?.email ?? userEmail;
      } catch (err) {
        logger.warn('User get-or-create failed during onboarding, continuing:', err);
      }

      const [, savedTemplateId] = await Promise.all([
        (async () => {
          try {
            const updatedUser = await mutateUpdateUser(updateData);
            userEmail = updatedUser?.data?.email ?? userEmail;
            if (resumeAfterAuthOnboard) {
              clearAuthOnboardAnswers();
              clearCachedOnboardingPendingMark();
            }
            if (formData.displayLanguage) {
              setDisplayLanguage(undefined);
            }
            if (isEligibleForPracticeTrialModal(organisationSize)) {
              setPracticeTrialModalPending(true);
            }
            await invalidateGamifyTrialExtension();
          } catch (err) {
            logger.warn('User profile update failed during onboarding, continuing:', err);
            // Silent (user-invisible) failure — but it means the user lands
            // on the app with stale profile data, so we want a count.
            track({
              options: { amplitude: false },
              event_name: TrackingEventName.OnboardingErrorViewed,
              props: {
                step: OnboardingErrorStep.PROFILE_UPDATE,
                error_type: ErrorType.API_ERROR,
                error_message: sanitizeErrorMessage(
                  err instanceof Error ? err.message : 'Unknown profile-update error'
                ),
                severity: ErrorSeverity.WARNING,
                country: formData.country?.countryCode,
                onboarding_variant: GET_ONBOARDING_V2_VARIANT(),
              },
            });
          }
        })(),
        saveGeneratedTemplateWithRetry({
          formData,
          mutateCreateTemplate,
          onFinalFailure: () => {
            toast({
              title: t('common.error'),
              description: t('signup.templateSaveFailed'),
              tone: 'destructive',
            });
            // User-visible destructive toast — track it so we know when
            // users see the "we couldn't save your template" message.
            track({
              options: { amplitude: false },
              event_name: TrackingEventName.OnboardingErrorViewed,
              props: {
                step: OnboardingErrorStep.TEMPLATE_SAVE,
                error_type: ErrorType.API_ERROR,
                error_message: 'Template save failed after retry',
                severity: ErrorSeverity.WARNING,
                country: formData.country?.countryCode,
                onboarding_variant: GET_ONBOARDING_V2_VARIANT(),
              },
            });
          },
        }),
      ]);

      await submitTemplateFavoritesSafe(submitFavorites, savedTemplateId);

      // Earlier flow steps (e.g. Learn Basics clinic step) may have captured a
      // HubSpot booking intent without committing it. Submit it now that the
      // user has actually completed onboarding.
      await submitDeferredMeetingBookingIfAny({
        bookingIntent: formData.bookingIntent,
        userEmail,
        bookMeetingAsync: mutateBookMeeting,
      });

      // Invite emails collected before completion — send after PATCH so the
      // Practice team exists. Best-effort: failure does not block exit.
      const inviteOutcome = await submitDeferredPracticeInvitesIfAny({
        inviteEmails: formData.inviteEmails,
        sendInvites,
        settleTeam: settlePracticeTeam,
      });
      if (inviteOutcome === 'sent') {
        toast({
          title: intl.formatMessage(inviteTeamStepMessages.invitesSentToast),
          tone: 'success',
        });
      } else if (inviteOutcome === 'partial') {
        toast({
          title: intl.formatMessage(inviteTeamStepMessages.invitesPartialToast),
          tone: 'destructive',
        });
      } else if (inviteOutcome === 'failed') {
        toast({
          title: intl.formatMessage(inviteTeamStepMessages.teamNotReadyToast),
          tone: 'destructive',
        });
        // Step was marked done on Continue; clear so PracticeTrialModal can
        // still chain PracticeInviteTeamModal as a retry surface.
        setInviteTeamStepCompleted(false);
      }

      const experience = resolveOnboardingExperience({
        countryCode: formData.country?.countryCode,
        countryName: formData.country?.name,
        languageCode: formData.displayLanguage,
        hasBookedDemo: getHasBookedDemo(),
      });

      submitDemoSignupIfEligible({
        formData,
        ctaOptIn,
        userEmail,
        experience,
        webinarTimeslot,
      });

      // Webinar variant also captures an optional phone + reason demo lead
      // (active webinar only; the booked-out fallback submits via
      // submitRequestDemoIfEligible below).
      submitWebinarDemoLeadIfEligible({
        formData,
        ctaOptIn,
        demoReason,
        userEmail,
        experience,
        organisationSize,
        webinarBookedOut,
      });

      submitRequestDemoIfEligible({
        formData,
        ctaOptIn,
        demoReason,
        userEmail,
        experience,
        organisationSize,
        webinarBookedOut,
      });

      trackOnboardingCompletionAnalytics({
        formData,
        organisationSize,
        analytics,
        baaRequired: !!baaRequired,
        complianceKitRequired: !!complianceKitRequired,
        phoneNumber,
      });

      clearStoredRefCode();
      clearStoredReferralCampaign();
      // Drop the resume record before any redirect: the flow is submitted, so a
      // later visit in this tab must start clean rather than replay old answers.
      clearOnboardingProgress();

      if (refreshedRegion && needsRegionRedirect(window.location.host, refreshedRegion)) {
        const regionalUrl = buildRegionalUrl(
          getHostForRegion(refreshedRegion),
          '/scribe'
        );
        window.location.assign(regionalUrl);
        return false;
      }

      setHasCompletedOnboarding(true);
      onSuccess?.();
      return true;
    } catch (err) {
      const caught = err instanceof Error ? err : new Error('Unknown error');
      logger.error('Failed to complete onboarding:', caught);
      setError(caught);
      onError?.(caught);

      track({
        options: { amplitude: false },
        event_name: TrackingEventName.OnboardingErrorViewed,
        props: {
          step: OnboardingErrorStep.SESSION_STARTED,
          error_type: ErrorType.API_ERROR,
          error_message: sanitizeErrorMessage(caught.message),
          severity: ErrorSeverity.CRITICAL,
          country: formData.country?.countryCode,
          onboarding_variant: GET_ONBOARDING_V2_VARIANT(),
        },
      });
      return false;
    } finally {
      setIsSubmitting(false);
      setIsOnboardingSubmitInProgress(false);
    }
  }, [
    formData,
    ctaOptIn,
    demoReason,
    user,
    mutateUpdateUser,
    mutateUpdateUserRegion,
    mutateCreateTemplate,
    templateQualityGate,
    mutateBookMeeting,
    sendInvites,
    setDisplayLanguage,
    webinarTimeslot,
    webinarBookedOut,
    phoneNumber,
    baaRequired,
    complianceKitRequired,
    signupSource,
    affiliateCode,
    refCode,
    referralCampaign,
    analytics,
    setHasCompletedOnboarding,
    setIsOnboardingSubmitInProgress,
    setPracticeTrialModalPending,
    setInviteTeamStepCompleted,
    submitFavorites,
    t,
    intl,
    onSuccess,
    onError,
    resumeAfterAuthOnboard,
  ]);

  return {
    completeOnboarding,
    isSubmitting,
    error,
  };
}
